// 
/**
 *
 * @author Jakub Ruffer <ruffer@fg.cz>
 */
import Uri from 'urijs';
import $ from 'jquery';
import { PartialUpdateSettings } from './PartialUpdateSettings';
import { removeFromData } from '../lib/form';


export const getComposedUrl = (url, target) => {
  if (Array.isArray(target)) {
    target = target.join('+');
  }
  target = encodeURIComponent(target);
  let index = url.indexOf('?');
  const indexHash = url.indexOf('#');
  if (indexHash > -1 && (indexHash < index || index === -1)) {
    index = indexHash;
  }
  if (index > -1) {
    return `${url.substr(0, index)}@${target}${url.substr(index)}`;
  }
  return `${url}@${target}`;
};

export const getDynamicAreaId = (uri) => {
  const hashIndex = uri.indexOf('#');
  const questionMarkIndex = uri.indexOf('?');
  const atIndex = uri.indexOf('@');
  const match = uri.match(/(@)(.*?(?=\?|#|$))/);

  if (atIndex > hashIndex && atIndex > questionMarkIndex && (
    hashIndex > -1 || questionMarkIndex > -1
  )) {
    return null;
  }
  if (questionMarkIndex > -1 && atIndex > questionMarkIndex) {
    return null;
  }
  if (match && match.length > 2) {
    return match[2];
  }
  return null;
};

const getDynamicAreaIdWithoutContextFromString = (areaId) => {
  const match = areaId.match(/ramJetLayoutContext_(.*)_(.*)/);
  return (match && match[2]) ? match[2] : areaId;
};

export const getDynamicAreaIdWithoutContext = (areaId) => {
  if (Array.isArray(areaId)) {
    return areaId.map((area) => getDynamicAreaIdWithoutContextFromString(area)).sort();
  }
  return getDynamicAreaIdWithoutContextFromString(areaId);
};

export const getDynamicAreaIdWithoutContextFromUrl = (uri) => {
  const areaId = getDynamicAreaId(uri);
  if (areaId) {
    return getDynamicAreaIdWithoutContext(areaId);
  }
  return areaId;
};

export const getElementIdWithContext = (target, contextId) =>
  `ramJetLayoutContext_${contextId}_${getDynamicAreaIdWithoutContext(target)}`;

export const getCleanUrl = (uri, optRemoveOrigin) => {
  const hashIndex = uri.indexOf('#');
  const questionMarkIndex = uri.indexOf('?');
  const atIndex = uri.indexOf('@');

  // jestliže se první index zavináče nachází za otazníkem nebo hashem, tak se nejedná o
  // indetifikátor widgetu, ale je pouze součástí hodnoty parametru nebo samotného názvu parametru
  if (atIndex > hashIndex && atIndex > questionMarkIndex && (
    hashIndex > -1 || questionMarkIndex > -1
  )) {
    return uri;
  }
  if (questionMarkIndex > -1 && atIndex > questionMarkIndex) {
    return uri;
  }

  if (optRemoveOrigin) {
    // eslint-disable-next-line
    uri = uri.replace(/^.*\/\/[^\/]+/, '');
  }

  return uri.replace(/(@.*?)(?=\?|#|$)/, '');
};

export const getRequestTarget = ({
  layoutContext,
  requestTarget,
}) => {
  if (requestTarget && layoutContext) {
    requestTarget = getElementIdWithContext(requestTarget, layoutContext);
  }
  return requestTarget;
};
export const getTargets = ({
  layoutContext,
  target,
  containerId,
}) => {
  let targets = target || containerId;
  if (targets == null) {
    return [];
  }
  if (!Array.isArray(targets)) {
    targets.replace(/,/g, '+');
    targets = targets.split('+');
  }
  if (layoutContext) {
    targets = targets.map((computedTarget) =>
      getElementIdWithContext(computedTarget, layoutContext),
    );
  }
  return targets;
};
export const getTarget = (settings) => {
  return getTargets(settings)
    .join('+');
};
export const getSubmitId = ({
  layoutContext,
  submitId,
}) => {
  if (submitId && layoutContext) {
    submitId = getElementIdWithContext(submitId, layoutContext);
  }
  return submitId;
};

export const removeDataFromUrl = (
  url,
  remove,
  likeWidgetData,
) => {
  if (!remove) {
    return url;
  }
  const uri = new Uri(url);
  const query = Uri.parseQuery(uri.query());
  let queryFormData = Object.keys(query)
    .map((key) => (
      {
        name: key,
        value: query[key],
      }
    ));
  queryFormData = removeFromData(queryFormData, remove, likeWidgetData);
  const newQuery = {};
  queryFormData.forEach(({ name, value }) => {
    newQuery[name] = value;
  });
  uri.query(newQuery);

  return uri.toString();
};

export const removeFormIdFromUrl = (url) => {
  const uri = new Uri(url);
  uri.removeQuery('formId');
  return uri.toString();
};

export const removeFormIdFromData = (
  data,
) =>
  data.filter(({ name }) => name !== 'formId');

export const parsePartialUpdateSettingsFromUrl = (url) => {
  const frameUri = new Uri(url);
  let innerPartialUpdate = null;
  if (frameUri.hasQuery('pu')) {
    const query = Uri.parseQuery(frameUri.query());
    const { pu } = query;
    try {
      innerPartialUpdate = (JSON.parse(pu));
      // eslint-disable-next-line
    } catch (err) {
    }
    return innerPartialUpdate;
  }
  return innerPartialUpdate;
};

export const parseResultsFromAjaxResult = (settings, result) => {
  let { target } = settings;
  target = getTargets(settings);
  if (target.length > 1) {
    const prefix = '<!-- RamJet-WidgetDelimiters:';
    const suffix = ' -->';
    const pIndex = result.indexOf(prefix);
    const sIndex = result.indexOf(suffix, pIndex + prefix.length);
    const results = {};
    if (pIndex > -1 && sIndex > -1) {
      const multipleWidgetDelimiters =
        result.substr(pIndex + prefix.length, sIndex - (pIndex + prefix.length));
      const widgetResultDelimiters = multipleWidgetDelimiters.split(',');
      let lastIndex = sIndex + suffix.length;
      widgetResultDelimiters.forEach((delimiter, index) => {
        const delimiterInHTMLComments = `<!-- ${delimiter} -->`;
        const nextIndex = result.indexOf(delimiterInHTMLComments);
        const singleResult = result.substr(lastIndex, nextIndex - lastIndex);
        lastIndex = nextIndex + delimiterInHTMLComments.length;
        results[target[index]] = singleResult;
      });
    } else {
      throw new Error(`Server returned unparseable result - expected response starting with: ${prefix} ${suffix}`);
    }
    return results;
  }

  return {
    [target[0]]: result,
  };
};

export const getJQuery = (element) => {
  if (typeof element === 'string') {
    return $(`#${element}`);
  }
  if (element instanceof $) {
    return element;
  }
  return $(element);
};

export const getParentPartialUpdateContainer = (element) => {
  return element.closest('.isPartialUpdateContainer');
};

export const getFileInputs = (form) => {
  // nebrat file inputy vyuzivane pluploadem, tedy ty co jsou uvnir elementu s css tridou plupload
  return $('input:file', form)
    .not('.plupload input:file');
};

export const isMultipart = (jForm) => {
  if (jForm.hasClass('noFileUpload')) {
    return false;
  }
  const hasFileInputs = getFileInputs(jForm).length > 0;
  const mp = 'multipart/form-data';
  const multipart = (
    jForm.attr('enctype') === mp || jForm.attr('encoding') === mp
  );
  return hasFileInputs || multipart;
};
export const isButtonElement = (element) => {
  return element.is(
    'input[type="submit"], input[type="image"], button[type="submit"], button[type="button"]');
};

export const getTargetElements = (settings) => {
  const targets = getTargets(settings);
  return targets.map((tar) => document.getElementById(tar));
}



// WEBPACK FOOTER //
// ./node_modules/fg-frontend/src/partialUpdate/partialUpdateUtils.js